/**
 * persist.js — Carousel Composer autosave.
 *
 * Photos are local files and live in the tab only, but the arrangement is saved
 * keyed by FILENAME, so re-adding the same photos brings the whole carousel back.
 * The JSON keeps the artifact's shape, so a v1 save from the standalone artifact
 * still restores here. v2 adds the HDPhotoHub media id next to the filename, so a
 * carousel saved to the database reopens the right frames anywhere:
 *
 *   { v:3, ts, preset, S:{ fmt, gap, pad, bg, qual, type, prefix },
 *     groups:[{ name, mid?, sid?, src?, n, start, oy }],
 *     photos:[{ name, mid?, sid?, src?, look:{ rot, corner, adj } }],
 *     slides:[{ layout, split?, pano:{ g:<groups index>, i }|null,
 *               cells:[{ name, mid?, sid?, src?, zoom, ox, oy, fit?, look? }|null] }] }
 *
 * v3 adds `photos` — the rotation and adjustments that live on a photo, so a
 * saved carousel reopens with the same look — and per-cell `fit` / `look:'none'`.
 */
import {
  CELLS, DEFAULT_DESIGN, DEFAULT_EXPORT_OPTS, cellCount, heroSplit, materializeCells,
} from './geometry.js';
import { makeImgById } from './library.js';
import { isPlainLook, normalizeLook } from './look.js';

export const SAVE_KEY = 'carousel-composer.state.v1';
export const UI_KEY = 'carousel-composer.ui.v1';

// Two people on one machine must not share an autosave, so the browser copy is
// keyed by whoever is signed in. The server draft is the record; this is the
// copy that survives an offline moment.
let scope = '';

// Returns true when the key actually changed, so a caller holding a cached read
// of the old key knows to throw it away.
export function setSaveScope(profileId) {
  const next = profileId ? ':' + profileId : '';
  if (next === scope) return false;
  scope = next;
  return true;
}

function saveKey() {
  return SAVE_KEY + scope;
}

// How a saved slide points back at a photo. Exported so a draft can name the
// photos in the selection tray the same way. A local file has only its filename;
// a shoot photo also carries its HDPhotoHub media id, which survives a rename and
// is what lets a saved carousel reopen the right frames on another machine.
export function photoRef(im) {
  if (!im) return { name: '' };
  return im.source === 'hdph'
    ? { name: im.name, mid: im.mid, sid: im.sid, src: 'hdph' }
    : { name: im.name };
}

// state: { slides, panos, design, exportOpts, library | imgById }
export function serializeState(state) {
  const { slides, design, exportOpts } = state;
  const panos = state.panos || {};
  const imgById = state.imgById || makeImgById(state.library || []);
  const groups = [], gidx = new Map();
  const out = slides.map((sl) => {
    let pano = null;
    const g = sl.pano ? panos[sl.pano.gid] : null;
    if (g) {
      if (!gidx.has(sl.pano.gid)) {
        const im = imgById(g.imgId);
        gidx.set(sl.pano.gid, groups.length);
        groups.push({ ...photoRef(im), n: g.n, start: g.start, oy: g.oy });
      }
      pano = { g: gidx.get(sl.pano.gid), i: sl.pano.i };
    }
    // a pano part saves the crop it is showing, as the artifact's synced cell did
    const cells = g ? materializeCells(sl, { panos, imgById, design }) : sl.cells;
    return {
      layout: sl.layout,
      split: sl.layout === 'hero3' ? heroSplit(sl) : undefined,
      pano,
      cells: cells.map((c) => {
        const im = c && imgById(c.imgId);
        if (!im) return null;
        const out = { ...photoRef(im), zoom: c.zoom, ox: c.ox, oy: c.oy };
        if (c.fit === 'contain') out.fit = 'contain';
        if (c.look === 'none') out.look = 'none';
        return out;
      }),
    };
  });
  // every photo a slide uses that carries a look of its own
  const photos = [], seen = new Set();
  slides.forEach((sl) => {
    sl.cells.forEach((c) => {
      const im = c && imgById(c.imgId);
      if (!im || seen.has(im.id) || isPlainLook(im.look)) return;
      seen.add(im.id);
      photos.push({ ...photoRef(im), look: normalizeLook(im.look) });
    });
  });
  (state.panos ? Object.values(state.panos) : []).forEach((g) => {
    const im = imgById(g.imgId);
    if (!im || seen.has(im.id) || isPlainLook(im.look)) return;
    seen.add(im.id);
    photos.push({ ...photoRef(im), look: normalizeLook(im.look) });
  });

  // `sel` is only passed for the per-user draft: the frames you had picked out
  // but not yet placed are part of where you were.
  const sel = (state.sel || []).map((id) => imgById(id)).filter(Boolean).map(photoRef);

  return {
    v: 3,
    ts: Date.now(),
    photos,
    ...(sel.length ? { sel } : null),
    preset: design.preset,
    S: {
      fmt: design.fmt, gap: design.gap, pad: design.pad, bg: design.bg,
      qual: exportOpts.qual, type: exportOpts.type, prefix: exportOpts.prefix,
      bundle: exportOpts.bundle,
    },
    groups,
    slides: out,
  };
}

// -> { slides, panos, design, exportOpts, matched } | null when no cell matched a
// loaded photo. current: { design, exportOpts } that saved settings merge over.
export function applyState(saved, library, uid, current = {}) {
  if (!saved || !saved.slides) return null;
  const byName = new Map(library.map((im) => [im.name, im.id]));
  const byMid = new Map(library.filter((im) => im.mid != null).map((im) => [im.mid, im.id]));
  // a shoot photo is matched by media id first: it is stable across renames
  const findId = (ref) => (ref && ((ref.mid != null && byMid.get(ref.mid)) || byName.get(ref.name))) || null;
  const gs = (saved.groups || []).map((g) => ({
    imgId: findId(g),
    n: g.n,
    start: g.start == null ? 0.5 : g.start,
    oy: g.oy == null ? 0.5 : g.oy,
  }));
  const gids = [];
  const panos = {};
  let matched = 0;
  const slides = [];
  saved.slides.forEach((sv) => {
    const cells = (sv.cells || [null]).map((c) => {
      const id = findId(c);
      if (!id) return null;
      matched++;
      const out = { imgId: id, zoom: c.zoom || 1, ox: c.ox == null ? 0.5 : c.ox, oy: c.oy == null ? 0.5 : c.oy };
      if (c.fit === 'contain') out.fit = 'contain';
      if (c.look === 'none') out.look = 'none';
      return out;
    });
    const layout = CELLS[sv.layout] ? sv.layout : 'single';
    const want = cellCount(layout);
    while (cells.length < want) cells.push(null);
    const sl = { id: uid(), layout, cells: cells.slice(0, want) };
    if (layout === 'hero3' && typeof sv.split === 'number') sl.split = sv.split;
    const g = sv.pano && gs[sv.pano.g];
    if (g && g.imgId) {
      if (!gids[sv.pano.g]) {
        gids[sv.pano.g] = uid();
        panos[gids[sv.pano.g]] = g;
      }
      sl.pano = { gid: gids[sv.pano.g], i: sv.pano.i };
    }
    slides.push(sl);
  });
  if (!matched) return null;

  const design = { ...DEFAULT_DESIGN, ...current.design };
  const exportOpts = { ...DEFAULT_EXPORT_OPTS, ...current.exportOpts };
  const S = saved.S;
  if (S) {
    design.fmt = S.fmt || design.fmt;
    design.gap = S.gap ?? design.gap;
    design.pad = S.pad ?? design.pad;
    design.bg = S.bg || design.bg;
    exportOpts.qual = S.qual || exportOpts.qual;
    exportOpts.prefix = S.prefix || exportOpts.prefix;
    exportOpts.type = S.type || exportOpts.type;
    exportOpts.bundle = S.bundle === 'zip' ? 'zip' : 'files';
    if (saved.preset) design.preset = saved.preset;
  }
  // looks are returned rather than applied: the caller owns the library
  const looks = (saved.photos || [])
    .map((pr) => ({ imgId: findId(pr), look: normalizeLook(pr.look) }))
    .filter((x) => x.imgId);
  const sel = (saved.sel || []).map(findId).filter(Boolean);
  return { slides, panos, design, exportOpts, matched, looks, sel };
}

/* ---------------- browser storage (every call is safe without it) ---------------- */

export function readSaved() {
  try {
    const raw = globalThis.localStorage.getItem(saveKey());
    if (!raw) return null;
    const st = JSON.parse(raw);
    return st && st.slides && st.slides.length ? st : null;
  } catch {
    return null;
  }
}

// An empty carousel never overwrites the last good save, so "Remove all slides"
// stays undoable through the restore banner. Returns true when written.
export function writeSaved(state) {
  try {
    if (!state.slides.length) return false;
    globalThis.localStorage.setItem(saveKey(), JSON.stringify(serializeState(state)));
    return true;
  } catch {
    return false;
  }
}

export function clearSaved() {
  try { globalThis.localStorage.removeItem(saveKey()); } catch { /* storage unavailable */ }
}

export function readUI() {
  try { return JSON.parse(globalThis.localStorage.getItem(UI_KEY) || '{}') || {}; } catch { return {}; }
}

export function writeUI(patch) {
  try { globalThis.localStorage.setItem(UI_KEY, JSON.stringify(Object.assign(readUI(), patch))); } catch { /* storage unavailable */ }
}
